import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactPlayer from "react-player";
import PosterPreview from "./PosterPreview";
import Button from "react-bootstrap/Button";
import PosterGallery from "./PosterGallery";
import { SESSIONS } from "./config";
import Form from "react-bootstrap/Form";
import { Typeahead } from "react-bootstrap-typeahead";
import useLocalStorage from "./hooks/useLocalStorage";
import AlertDismissible from "./AlertDismissible";
import { Play, Stop } from "react-bootstrap-icons";
import { LanguageContext } from "./i18n/context";

const sortPostersByTitleAZ = (a, b) => {
  let nameA = a.title.toUpperCase();
  let nameB = b.title.toUpperCase();
  if (nameA < nameB) {
    return -1;
  } else if (nameA > nameB) {
    return 1;
  } else {
    return 0;
  }
};

const filterPosters = (poster, filterArray) => {
  if (filterArray.length === 0) {
    return false;
  }

  if (filterArray.length === 1 && filterArray[0] === "all") {
    return true;
  }

  return filterArray.includes(poster.id);
};

const AllPosters = ({ config, header = null }) => {
  const playerRef = useRef();
  const [isRecordingPlaying, setIsRecordingPlaying] = useState(false);
  const [showInstructions, setShowInstructions] = useLocalStorage(
    "posters-instructions",
    true
  );

  const [currentPoster, setCurrentPoster] = useState(-1);
  const [showSessions, setShowSessions] = useState(
    SESSIONS.split(",").reduce(function (acc, cur) {
      acc[cur] = cur;
      return acc;
    }, {})
  );
  const [postersInTopicFilter, setPostersInTopicFilter] = useState(["all"]);
  const sessions = Object.values(config.themes || []).filter(
    (session) => SESSIONS.split(",").indexOf(`${session.id}`) > -1
  );
  const posters = sessions
    .filter((s) => showSessions[s.id])
    .map((x) => Object.values(x.posters))
    .flat();

  const searchOptions = sessions
    .filter((s) => showSessions[s.id])
    .map((x) => Object.values(x.posters))
    .flat()
    .sort(sortPostersByTitleAZ)
    .map((x, idx) => [
      { id: x.id, value: x.authors, idx, type: "posterId" },
      { id: x.id, value: x.title, idx, type: "posterId" },
    ])
    .flat();

  const topics = Object.values(config.topics || {}).map((t) => ({
    value: t.title,
    posters: Object.keys(t.posters),
    type: "topic",
  }));

  useEffect(() => {
    const iFrameInUse = window.location !== window.parent.location;
    console.log(`Displayed in iframe?: ${iFrameInUse}`);
  }, []);

  useEffect(() => {
    if (currentPoster === -1) {
      setPostersInTopicFilter(["all"]);
    }
  }, [currentPoster]);
  const sanitizeFacebook = useCallback((facebook) => {
    if (facebook.startsWith("https")) return facebook;
    return `https://${facebook}`;
  }, []);
  const sanitizeLinkedIn = useCallback((linkedIn) => {
    if (linkedIn.startsWith("https")) return linkedIn;
    return `https://${linkedIn}`;
  }, []);
  const sanitizeTwitter = useCallback((twitter) => {
    if (twitter.startsWith("https")) return twitter;
    if (twitter[0] === "@") {
      return `https://twitter.com/${twitter}`;
    }
    return `https://${twitter}`;
  }, []);

  if (currentPoster === -1)
    return (
      <LanguageContext.Consumer>
        {({ language, changeLanguage }) => (
          <div className="poster-session sessions-with-sidebar">
            {header && header}

            <div className="sidebar">
              <div className="with-language">
                <Button onClick={() => changeLanguage()}>{language}</Button>
                <Form style={{ width: "97%" }}>
                  <Form.Group>
                    <Typeahead
                      id="basic-typeahead-single"
                      minLength={3}
                      labelKey="value"
                      onChange={(e) => {
                        if (!e || !e[0]) {
                          setPostersInTopicFilter(["all"]);
                          return;
                        }
                        if (e[0].type === "posterId") {
                          setCurrentPoster(e[0].idx);
                        } else if (e[0].type === "topic") {
                          setPostersInTopicFilter(e[0].posters);
                        }
                      }}
                      options={[...searchOptions, ...topics]}
                      placeholder={
                        language === "PL"
                          ? "Wyszukaj plakat, temat lub autora..."
                          : "Search for a poster, topic or an author..."
                      }
                      emptyLabel={
                        language === "PL"
                          ? "Brak plakatów"
                          : "There are no posters meeting search criteria"
                      }
                    />
                  </Form.Group>
                </Form>
              </div>
              <Form>
                <Form.Label>
                  <b>
                    {language === "PL" ? "Sesje plakatowe" : "Posters Sessions"}
                  </b>
                </Form.Label>
                <div className="sessions-checkboxes">
                  {config.themes &&
                    Object.values(config.themes).map((theme) => {
                      return (
                        <Form.Check key={theme.id}>
                          <Form.Check.Input
                            defaultChecked={!!showSessions[theme.id]}
                            onClick={() => {
                              if (!showSessions[theme.id]) {
                                showSessions[theme.id] = theme.id;
                              } else {
                                delete showSessions[theme.id];
                              }
                              setShowSessions({ ...showSessions });
                            }}
                            id={theme.id}
                            type="checkbox"
                          />
                          <Form.Check.Label htmlFor={theme.id}>
                            {`${theme.title} (${
                              Object.keys(theme.posters).length
                            })`}
                          </Form.Check.Label>
                        </Form.Check>
                      );
                    })}
                </div>
              </Form>
            </div>
            <div className="session-posters wrap">
              {showInstructions && config.instructions && (
                <AlertDismissible
                  show={showInstructions}
                  config={config.instructions}
                  setShowInstructions={setShowInstructions}
                />
              )}
              {posters
                .filter((poster) => filterPosters(poster, postersInTopicFilter))
                .sort(sortPostersByTitleAZ)
                .map((poster, idx) => (
                  <div className="flex-item" key={poster.id}>
                    <PosterPreview
                      poster={poster}
                      onClick={() => setCurrentPoster(idx)}
                    />
                  </div>
                ))}
            </div>
          </div>
        )}
      </LanguageContext.Consumer>
    );

  const poster = posters
    .filter((poster) => filterPosters(poster, postersInTopicFilter))
    .sort(sortPostersByTitleAZ)[currentPoster];

  const recordingUrl = poster.pages.find(
    (x) => x.indexOf("mp3") > -1 || x.indexOf("mp4") > -1
  );

  return (
    <LanguageContext.Consumer>
      {({ language, changeLanguage }) => (
        <div className="poster-session scroll">
          <Button className="back-button" onClick={() => setCurrentPoster(-1)}>
            <i className="fas fa-arrow-left" />
          </Button>
          <div className="title-with-buttons">
            <h2 className="poster-title">{poster.title}</h2>
            <p>{poster.authors}</p>
            {poster.contactDetails && (
              <div className="contact-details">
                {poster.contactDetails.fullName && (
                  <div className="contact-details-item">{`${
                    language === "PL" ? "Kontakt" : "Contact person"
                  }: ${poster.contactDetails.fullName}`}</div>
                )}
                {poster.contactDetails.email && (
                  <div className="contact-details-item">
                    Email:{" "}
                    <a href={`mailto:${poster.contactDetails.email}`}>
                      {poster.contactDetails.email}
                    </a>{" "}
                  </div>
                )}
                {poster.contactDetails.facebook && (
                  <div className="contact-details-item">
                    Facebook:{" "}
                    <a
                      href={sanitizeFacebook(poster.contactDetails.facebook)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {poster.contactDetails.facebook}
                    </a>
                  </div>
                )}
                {poster.contactDetails.linkedIn && (
                  <div className="contact-details-item">
                    LinkedIn:{" "}
                    <a
                      href={sanitizeLinkedIn(poster.contactDetails.linkedIn)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {poster.contactDetails.linkedIn}
                    </a>
                  </div>
                )}
                {poster.contactDetails.twitter && (
                  <div className="contact-details-item">
                    Twitter:{" "}
                    <a
                      href={sanitizeTwitter(poster.contactDetails.twitter)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {poster.contactDetails.twitter}
                    </a>
                  </div>
                )}
              </div>
            )}
            {recordingUrl && (
              <>
                <div>
                  <Button
                    className="playback-button"
                    onClick={() => setIsRecordingPlaying((s) => !s)}
                  >
                    {isRecordingPlaying ? (
                      <>
                        <Stop className="playback-icon" />{" "}
                        {language === "PL" ? "Zatrzymaj" : "Stop"}
                      </>
                    ) : (
                      <>
                        <Play className="playback-icon" />{" "}
                        {language === "PL"
                          ? "Wysłuchaj prezentacji Autora"
                          : "Listen to the recording"}
                      </>
                    )}
                  </Button>
                </div>
                <ReactPlayer
                  ref={playerRef}
                  url={recordingUrl}
                  width="0px"
                  height="0px"
                  playing={isRecordingPlaying}
                />
              </>
            )}
            <div style={{ padding: "1rem" }}>
              <Button
                variant={currentPoster === 0 ? "dark" : "primary"}
                disabled={currentPoster === 0}
                onClick={() => setCurrentPoster((current) => current - 1)}
              >
                {language === "PL" ? "Poprzedni plakat" : "Previous poster"}
              </Button>
              <Button
                variant={
                  currentPoster ===
                  posters.filter((poster) =>
                    filterPosters(poster, postersInTopicFilter)
                  ).length -
                    1
                    ? "dark"
                    : "primary"
                }
                disabled={
                  currentPoster ===
                  posters.filter((poster) =>
                    filterPosters(poster, postersInTopicFilter)
                  ).length -
                    1
                }
                onClick={() => setCurrentPoster((current) => current + 1)}
              >
                {language === "PL" ? "Następny plakat" : "Next poster"}
              </Button>
            </div>
          </div>
          <div className="poster-with-buttons">
            <PosterGallery session={poster.id} poster={poster} />
          </div>
        </div>
      )}
    </LanguageContext.Consumer>
  );
};

export default AllPosters;
